import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, LogIn, User, LogOut, Gamepad2, Crown, BookOpen, Image, ChevronDown } from 'lucide-react';
import { DiscordIcon } from './icons/SvgIcons';
import ThemeToggle from '@/components/ui/theme-toggle';
import { Button } from '@/components/ui/button';

import { useAuth } from '@/lib/auth-context';
import { usePassCodeur } from '@/lib/pass-codeur-context';
import { useToast } from '@/lib/use-toast';
import { cn } from '@/lib/utils';

import UserAvatar from '@/components/UserAvatar';
import { EXTERNAL_LINKS } from '../constants';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isResourcesDropdownOpen, setIsResourcesDropdownOpen] = useState(false);
  const { currentUser, logout, userData } = useAuth();
  const { hasAccessToCourses } = usePassCodeur();
  const navigate = useNavigate();
  const { toast } = useToast();
  const resourcesDropdownRef = useRef(null);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleNavLinkClick = () => {
    setIsOpen(false);
    setIsResourcesDropdownOpen(false);
  };

  const toggleResourcesDropdown = () => {
    setIsResourcesDropdownOpen(!isResourcesDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Ne pas fermer si on clique sur un lien externe
      if (event.target.tagName === 'A' && event.target.target === '_blank') {
        return;
      }
      
      // Ne pas fermer si on clique sur le bouton toggle en version mobile
      if (event.target.closest('button')?.title === 'Ressources et outils') {
        return;
      }
      
      if (resourcesDropdownRef.current && !resourcesDropdownRef.current.contains(event.target)) {
        setIsResourcesDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: "Déconnexion réussie",
        description: "Vous êtes maintenant déconnecté.",
        variant: "success",
      });
      navigate('/login');
    } catch (error) {
      console.error("Erreur de déconnexion:", error.message);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la déconnexion.",
        variant: "destructive",
      });
    }
  };



  // Si l'utilisateur n'a pas d'alias, rediriger vers /projets
  const projetsLink = userData?.alias ? `/project/${userData.alias}` : '/project';
  // Lien vers les jeux publics de l'utilisateur - accessible à tous
  const jeuxLink = userData?.alias ? `/play/${userData.alias}` : '/play';

  // Remplacer NavigationMenuItem par un div avec les mêmes styles
  const NavigationMenuItem = ({ children, className, ...props }) => (
    <div className={cn("flex items-center", className)} {...props}>
      {children}
    </div>
  );

  return (
    <nav className="bg-background border-b border-border sticky top-0 z-40 w-full">
      <div className="w-full px-6">
        <div className="flex justify-between items-center h-16">
          {/* Logo et menu à gauche */}
          <div className="flex items-center">
            <Link 
              to="/" 
              className="font-bold text-xl text-primary flex items-center gap-2 hover:opacity-80 transition-opacity"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <img src="/icon.png" alt="Logo" className="h-12 w-12 rounded" />
              <span>Mon Premier Jeu</span>
            </Link>
            <div className="hidden md:flex md:ml-8 md:items-center md:space-x-4">
              <NavigationMenuItem>
                <Link
                  to="/courses"
                  className={cn(
                    "group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50",
                    location.pathname.startsWith('/courses') && "bg-accent/100"
                  )}
                  onClick={handleNavLinkClick}
                >
                  Les Cours
                </Link>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <Link
                  to={projetsLink}
                  className={cn(
                    "group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50",
                    location.pathname.startsWith('/project') && "bg-accent/100"
                  )}
                >
                  Mes Projets
                </Link>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <Link
                  to={jeuxLink}
                  className={cn(
                    "group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50",
                    location.pathname.startsWith('/play') && "bg-accent/100"
                  )}
                >
                  Mes Jeux
                </Link>
              </NavigationMenuItem>

            </div>
          </div>

          {/* Boutons de login/profile et thème à droite */}
          <div className="flex items-center">
            {/* Desktop buttons */}
            <div className="hidden md:flex md:items-center md:gap-4">
              {(!currentUser || !hasAccessToCourses()) && (
                <Link 
                  to="/pass-codeur" 
                  className="px-4 py-2 rounded-md text-sm font-medium pass-codeur-btn shadow-md hover:shadow-lg flex items-center gap-2"
                >
                  <Crown className="h-4 w-4 pass-codeur-icon" />
                  Pass Codeur
                </Link>
              )}
              
              <Link 
                to={EXTERNAL_LINKS.DISCORD} 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 rounded-md hover:bg-muted transition-colors"
                title="Rejoindre Discord"
              >
                <DiscordIcon className="h-5 w-5 text-black dark:text-white" />
              </Link>
              
              {/* Resources Dropdown */}
              <div className="relative" ref={resourcesDropdownRef}>
                <button
                  onClick={toggleResourcesDropdown}
                  className="p-2 rounded-md hover:bg-muted transition-colors flex items-center gap-1"
                  title="Ressources et outils"
                >
                  <BookOpen className="h-5 w-5 text-black dark:text-white" />
                  <ChevronDown className={`h-3 w-3 text-black dark:text-white transition-transform ${isResourcesDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isResourcesDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-background border border-border rounded-md shadow-lg z-50">
                    <div className="py-2">
                      <div className="px-4 py-2 text-sm font-semibold text-muted-foreground border-b border-border">
                      Documentation
                      </div>
                      <Link
                        to="https://p5js.org/reference/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                        onClick={() => setIsResourcesDropdownOpen(false)}
                      >
                        📖 Documentation p5.js
                      </Link>
                      <div className="px-4 py-2 text-sm font-semibold text-muted-foreground border-b border-border">
                        Assets 2D Gratuits
                      </div>
                      <Link
                        to="https://opengameart.org/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                        onClick={() => setIsResourcesDropdownOpen(false)}
                      >
                        🎨 OpenGameArt.org
                      </Link>
                      <Link
                        to="https://www.gamedevmarket.net/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                        onClick={() => setIsResourcesDropdownOpen(false)}
                      >
                        🛒 GameDev Market
                      </Link>
                      <Link
                        to="https://itch.io/game-assets/free/tag-2d"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                        onClick={() => setIsResourcesDropdownOpen(false)}
                      >
                        🕹️ Itch.io Assets
                      </Link>
                      <div className="px-4 py-2 text-sm font-semibold text-muted-foreground border-b border-border">
                        Outils de Développement
                      </div>
                      <Link
                        to="https://htmlcolorcodes.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                        onClick={() => setIsResourcesDropdownOpen(false)}
                      >
                        🎨 Color Picker RGB
                      </Link>
                    </div>
                  </div>
                )}
              </div>
              
              {/* <ThemeToggle /> */}
              
              {currentUser ? (
                <Link to="/profile">
                  <Button variant="ghost" className="relative h-8 w-8 rounded-full overflow-hidden">
                    <UserAvatar user={currentUser} size="h-8 w-8" />
                  </Button>
                </Link>
              ) : (
                <Button onClick={() => navigate('/login')} className="gap-2" variant="outline">
                  <LogIn className="h-4 w-4" />
                  Se connecter
                </Button>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="flex items-center md:hidden gap-2">
              <button
                onClick={toggleMenu}
                className="ml-2 inline-flex items-center justify-center p-2 rounded-md hover:bg-muted transition-colors"
                aria-expanded={isOpen}
              >
                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div 
          className="md:hidden border-t border-border animate-in slide-in-from-top-2 duration-200"
        >
          <div className="w-full px-6">
            <div className="pt-2 pb-3 space-y-1">
              <Link
                to="/courses"
                className={cn(
                  "flex items-center px-4 py-3 text-base font-medium rounded-md hover:bg-muted/70 transition-colors",
                  location.pathname && location.pathname.startsWith('/courses') && "bg-accent/100"
                )}
                onClick={handleNavLinkClick}
              >
                Les Cours
              </Link>
              <Link
                to={projetsLink}
                className={cn(
                  "flex items-center px-4 py-3 text-base font-medium rounded-md hover:bg-muted/70 transition-colors",
                  location.pathname && location.pathname.startsWith('/project') && "bg-accent/100"
                )}
                onClick={handleNavLinkClick}
              >
                Mes Projets
              </Link>
              <Link
                to={jeuxLink}
                className={cn(
                  "flex items-center px-4 py-3 text-base font-medium rounded-md hover:bg-muted/70 transition-colors",
                  location.pathname && location.pathname.startsWith('/play') && "bg-accent/100"
                )}
                onClick={handleNavLinkClick}
              >
                Mes Jeux
              </Link>

              {/* Pass Codeur pour mobile */}
              {(!currentUser || !hasAccessToCourses()) && (
                <Link
                  to="/pass-codeur"
                  className="flex items-center px-4 py-3 text-base font-medium rounded-md hover:bg-muted/70 transition-colors pass-codeur-btn"
                  onClick={handleNavLinkClick}
                >
                  <Crown className="mr-2 h-5 w-5 pass-codeur-icon" />
                  Pass Codeur
                </Link>
              )}
              
              {/* Divider */}
              <div className="border-t border-border my-2"></div>
              
              {/* Discord, Resources and Theme Toggle */}
              <div className="flex items-center gap-4 px-4 py-3">
                <Link 
                  to={EXTERNAL_LINKS.DISCORD} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2 rounded-md hover:bg-muted transition-colors"
                  title="Rejoindre Discord"
                  onClick={handleNavLinkClick}
                >
                  <DiscordIcon className="h-5 w-5 text-black dark:text-white" />
                </Link>
                
                <div className="relative">
                  <button
                    onClick={toggleResourcesDropdown}
                    className="p-2 rounded-md hover:bg-muted transition-colors flex items-center gap-1"
                    title="Ressources et outils"
                  >
                    <BookOpen className="h-5 w-5 text-black dark:text-white" />
                    <ChevronDown className={`h-3 w-3 text-black dark:text-white transition-transform ${isResourcesDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>
                
                {/* <ThemeToggle /> */}
              </div>
              
              {/* Resources Dropdown Mobile */}
              {isResourcesDropdownOpen && (
                <div 
                  className="px-4 py-2 bg-muted/30 rounded-md mx-4 mb-2"
                  onClick={(e) => {
                    // Si on clique sur un lien externe, l'ouvrir puis fermer le dropdown
                    if (e.target.tagName === 'A' || e.target.closest('a')) {
                      e.stopPropagation();
                      // Fermer le dropdown après un court délai pour laisser le lien s'ouvrir
                      setTimeout(() => {
                        setIsResourcesDropdownOpen(false);
                      }, 200);
                    }
                  }}
                >
                  <div className="text-sm font-semibold text-muted-foreground mb-2">
                  Documentation
                  </div>
                  <div className="space-y-1">
                    <a
                      href="https://p5js.org/reference/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-2 py-1 text-sm hover:bg-accent hover:text-accent-foreground rounded transition-colors"
                    >
                      📖 Documentation p5.js
                    </a>
                  </div>
                  <div className="text-sm font-semibold text-muted-foreground mb-2 mt-3">
                    Assets 2D Gratuits
                  </div>
                  <div className="space-y-1">
                    <a
                      href="https://opengameart.org/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-2 py-1 text-sm hover:bg-accent hover:text-accent-foreground rounded transition-colors"
                    >
                      🎨 OpenGameArt.org
                    </a>
                    <a
                      href="https://www.gamedevmarket.net/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-2 py-1 text-sm hover:bg-accent hover:text-accent-foreground rounded transition-colors"
                    >
                      🛒 GameDev Market
                    </a>
                    <a
                      href="https://itch.io/game-assets/free/tag-2d"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-2 py-1 text-sm hover:bg-accent hover:text-accent-foreground rounded transition-colors"
                    >
                      🕹️ Itch.io Assets
                    </a>
                  </div>
                  <div className="text-sm font-semibold text-muted-foreground mb-2 mt-3">
                    Outils de Développement
                  </div>
                  <div className="space-y-1">
                    <a
                      href="https://htmlcolorcodes.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-2 py-1 text-sm hover:bg-accent hover:text-accent-foreground rounded transition-colors"
                    >
                      🎨 Color Picker RGB
                    </a>
                  </div>
                </div>
              )}
              
              {/* Divider */}
              <div className="border-t border-border my-2"></div>
              
              {/* User actions */}
              {currentUser ? (
                <>
                  <Link
                    to="/profile"
                    className="flex items-center px-4 py-3 text-base font-medium rounded-md hover:bg-muted/70 transition-colors"
                    onClick={handleNavLinkClick}
                  >
                    <User className="mr-2 h-5 w-5" />
                    Profil
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-3 text-base font-medium rounded-md hover:bg-muted/70 transition-colors"
                  >
                    <LogOut className="mr-2 h-5 w-5" />
                    Se déconnecter
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="flex items-center px-4 py-3 text-base font-medium rounded-md hover:bg-muted/70 transition-colors"
                  onClick={handleNavLinkClick}
                >
                  <LogIn className="mr-2 h-5 w-5" />
                  Se connecter
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;